import type { PlayerState, BallState } from '../sim/state';
import type { CollisionConfig, SimConfig } from '../config/simConfig';
import type { ActionKind, EntityId, Vec2, Tick } from '../types';
import { rngStep } from '../math/rng';
import { NO_TOUCH } from '../sim/possession';
import { trapWillRun } from '../sim/tackles';

/**
 * Internal per-tick contact record (§5.5), consumed by the referee foul evaluator (§7.1).
 * NOT a SimEvent — it never leaves the tick and is not on the wire.
 */
export interface ContactEvent {
  a: EntityId;
  b: EntityId;
  impulse: number; // physics impulse + action-added impulse (§5.5)
  n: Vec2; // contact normal, from a → b
  relSpeed: number; // closing speed along n
  aAction: ActionKind;
  bAction: ActionKind;
  positions: [Vec2, Vec2];
  ballFirst?: boolean; // for tackle contacts: did the tackle reach the ball first? (§7.1, set by annotateTackleContacts)
}

function actionImpulse(cfg: CollisionConfig, action: ActionKind): number {
  return cfg.ACTION_IMPULSE[action] ?? 0;
}

/**
 * Resolve player↔player (and player↔ref, when the ref is in the list) capsule collisions as 2D
 * circle de-penetration (§5.5): SOLVER_ITERS positional passes over all pairs, then one velocity
 * impulse pass over the pairs that overlapped, emitting a ContactEvent each. Mutates `players`.
 */
export function resolvePlayerCollisions(players: PlayerState[], cfg: CollisionConfig, braceMul = 1): ContactEvent[] {
  const twoR = cfg.CAPSULE_R * 2;
  // D82: a BLOCK player is BRACED — a heavier body. He gets a smaller inverse-mass (braceMul < 1), so
  // in both the positional de-penetration and the velocity impulse he moves LESS (holds his ground) and
  // the other body takes the larger share (the attacker visibly bounces). braceMul 1 ⇒ the old 50/50.
  const invMass = (p: PlayerState): number => (p.action === 'BLOCK' ? braceMul : 1);
  // D62: sent-off players are off the pitch and are NOT bodies. They used to be de-penetrated against
  // each other on the shared bench point forever, emitting contacts the referee read as fouls.
  const active = players.filter((p) => !p.sentOff);
  players = active;
  const n = players.length;
  const overlapped = new Map<number, [number, number]>();

  for (let iter = 0; iter < cfg.SOLVER_ITERS; iter++) {
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const pi = players[i]!;
        const pj = players[j]!;
        const dx = pj.pos.x - pi.pos.x;
        const dy = pj.pos.y - pi.pos.y;
        const dist = Math.hypot(dx, dy);
        if (dist >= twoR) continue;
        const nx = dist > 0 ? dx / dist : 1;
        const ny = dist > 0 ? dy / dist : 0;
        const wi = invMass(pi);
        const wj = invMass(pj);
        const total = twoR - dist;
        const ci = total * (wi / (wi + wj));
        const cj = total * (wj / (wi + wj));
        pi.pos.x -= nx * ci;
        pi.pos.y -= ny * ci;
        pj.pos.x += nx * cj;
        pj.pos.y += ny * cj;
        overlapped.set(i * n + j, [i, j]);
      }
    }
  }

  const events: ContactEvent[] = [];
  for (const [i, j] of overlapped.values()) {
    const pi = players[i]!;
    const pj = players[j]!;
    const dx = pj.pos.x - pi.pos.x;
    const dy = pj.pos.y - pi.pos.y;
    const dist = Math.hypot(dx, dy);
    const nx = dist > 0 ? dx / dist : 1;
    const ny = dist > 0 ? dy / dist : 0;
    // impulse j = −(1+e_p)·relVel·n / 2 (mass-1 each ⇒ /2), applied only when approaching
    const vn = (pj.vel.x - pi.vel.x) * nx + (pj.vel.y - pi.vel.y) * ny;
    let jimp = 0;
    if (vn < 0) {
      const wi = invMass(pi);
      const wj = invMass(pj);
      jimp = (-(1 + cfg.E_P) * vn) / (wi + wj); // D82: braced ⇒ heavier ⇒ the mobile body absorbs more (bounces)
      pi.vel.x -= nx * jimp * wi;
      pi.vel.y -= ny * jimp * wi;
      pj.vel.x += nx * jimp * wj;
      pj.vel.y += ny * jimp * wj;
    }
    const impulse = jimp + actionImpulse(cfg, pi.action) + actionImpulse(cfg, pj.action);
    events.push({
      a: pi.id,
      b: pj.id,
      impulse,
      n: { x: nx, y: ny },
      relSpeed: Math.abs(vn),
      aAction: pi.action,
      bAction: pj.action,
      positions: [
        { x: pi.pos.x, y: pi.pos.y },
        { x: pj.pos.x, y: pj.pos.y },
      ],
    });
  }
  return events;
}

/**
 * Ball↔player collision for NON-possessing players (§5.5): the ball bounces off the capsule with
 * restitution BALL_PLAYER_REST and records lastTouch. Possession claiming (slow, close ball) is the
 * separate §5.6 path. Mutates `ball` (and draws state.rng for the D52 through-the-legs roll).
 *
 * D52: the test is SWEPT — the segment the ball travelled this tick vs the capsule, not just the end
 * position. A 26 m/s pass moves ~0.9 m/tick and a 34 m/s shot ~1.1 m against a 0.46 m contact radius:
 * the old point test let fast balls TUNNEL straight through bodies ("players are not collision objects
 * for the ball"). A LOW crossing ball (z < THROUGH_LEGS_Z at contact) sometimes slips between the legs
 * (seeded roll, THROUGH_LEGS_P — deterministic, ascending entity-id draw order); otherwise it deflects,
 * and a defender on the shot line is a real obstacle. The KICKER during his own kick's free window is
 * exempt — you cannot block your own pass with your own legs.
 */
export function resolveBallPlayerCollisions(
  state: { ball: BallState; players: readonly PlayerState[]; rng: number },
  cfg: SimConfig,
  tick: Tick,
  prevBall?: { x: number; y: number; z: number },
): void {
  const ball = state.ball;
  const reach = cfg.ball.RADIUS + cfg.collision.CAPSULE_R;
  const p0 = prevBall ?? { x: ball.pos.x, y: ball.pos.y, z: ball.pos.z };
  const sx = ball.pos.x - p0.x;
  const sy = ball.pos.y - p0.y;
  const segLen2 = sx * sx + sy * sy;
  const ballSpeed = Math.hypot(ball.vel.x, ball.vel.y, ball.vel.z);
  for (const p of state.players) {
    if (p.id === ball.possessor) continue; // possessor handled by §5.6
    if (p.sentOff) continue;
    if (ball.kickedBy === p.id && ball.claimCooldownTicks > 0) continue; // D52: his own fresh kick
    // D52: a CONTROLLABLE ball (below CLAIM_MAX) belongs to the possession/trap system — the player
    // gathers it, he doesn't ricochet it off his shins (the swept deflect would otherwise fire in
    // step 5, before the step-6 claim, and every firm pass would bounce off its own receiver). The
    // body stays a pure obstacle for screamers and for players who CANNOT control the ball (§5.6
    // no-touch actions: prone, mid-punch, diving…).
    // D82: a BLOCK body is a WALL even to a controllable ball — an opponent's dribble touch bounces off
    // the braced blocker (the physical reflection below). Friendly-fire is physical too: a blocker deflects
    // ANY ball that hits him (simpler and honest — a braced body doesn't choose which balls it stops).
    // D149: …but ONLY where the possession system actually has jurisdiction. A claim needs
    // `z < CLAIM_Z` and a trap reaches `GATHER_Z`; the body capsule is CAPSULE_H (1.8 m) tall. Between
    // those, at chest and head height — exactly where a lofted pass arrives — no gathering verb existed
    // and the exemption still fired, so the ball flew THROUGH the man with no touch and no deflection.
    // Report 4 of playtest 5: "went straight through my team mate as if he wasn't there."
    // The exemption is only honest where a gathering verb will ACTUALLY run this tick. A passive player
    // claims below CLAIM_Z; the trap reaches GATHER_Z but only for a player who is pressing it. Between
    // the two, a player standing still has no verb at all — D149's first cut used GATHER_Z flat and left
    // that 0.25 m slot open, which is why a 18 m/s pass arriving at 0.71 m still went through the man.
    // D155: …and "pressing it" is four conditions, not one. `p.action === 'TRAP'` was the only one
    // checked here, so a trapper outside `trap.WINDOW` (ticks 0 and 7 of the 8) or one `claimBarred`
    // from the ball — who gathers nothing, all action long — was still exempt from his own body. The
    // predicate is `trapWillRun`, read by resolveTrap itself, so the two can no longer drift apart.
    const gatherable = ball.pos.z < cfg.possession.CLAIM_Z || (ball.pos.z < cfg.possession.GATHER_Z && trapWillRun(state, p, cfg));
    if (gatherable && ballSpeed < cfg.possession.CLAIM_MAX_SPEED && !NO_TOUCH.has(p.action) && p.action !== 'BLOCK') continue;
    // closest point of this tick's travel segment to the capsule axis (2D; t ∈ [0,1])
    const t = segLen2 > 1e-9 ? Math.max(0, Math.min(1, ((p.pos.x - p0.x) * sx + (p.pos.y - p0.y) * sy) / segLen2)) : 1;
    const cx = p0.x + sx * t;
    const cy = p0.y + sy * t;
    const zAt = p0.z + (ball.pos.z - p0.z) * t;
    if (zAt > cfg.collision.CAPSULE_H) continue; // ball flying over the capsule
    const dx = cx - p.pos.x;
    const dy = cy - p.pos.y;
    const dist = Math.hypot(dx, dy);
    if (dist >= reach) continue;
    // D52 through-the-legs: a low crossing ball sometimes nutmegs the body — no touch, no deflection.
    // One seeded draw per would-be contact (normative order: ascending entity id within step 5).
    if (zAt < cfg.collision.THROUGH_LEGS_Z && cfg.collision.THROUGH_LEGS_P > 0) {
      const { value, state: rs } = rngStep(state.rng);
      state.rng = rs;
      if (value < cfg.collision.THROUGH_LEGS_P) continue; // slipped between the legs
    }
    // D52: did the ball pass FROM the near side TO the far side this tick (true tunneling)? Then the
    // block is head-on — normal opposes the travel direction (the radial normal is degenerate/behind
    // for a crossing that already exited). Legacy overlap cases keep the radial normal + vn<0 gate,
    // so a slow ball drifting AWAY from an overlapped body is still only de-penetrated, never reflected.
    const a0 = (p0.x - p.pos.x) * sx + (p0.y - p.pos.y) * sy;
    const a1 = (ball.pos.x - p.pos.x) * sx + (ball.pos.y - p.pos.y) * sy;
    const crossedThrough = segLen2 > 1e-9 && a0 < 0 && a1 > 0;
    let nx: number;
    let ny: number;
    if (crossedThrough) {
      const L = Math.sqrt(segLen2);
      nx = -sx / L;
      ny = -sy / L;
    } else {
      nx = dist > 0 ? dx / dist : 1;
      ny = dist > 0 ? dy / dist : 0;
    }
    ball.pos.x = p.pos.x + nx * reach; // de-penetrate to the surface (on the blocked side)
    ball.pos.y = p.pos.y + ny * reach;
    const vn = ball.vel.x * nx + ball.vel.y * ny;
    if (vn < 0) {
      const k = (1 + cfg.collision.BALL_PLAYER_REST) * vn;
      ball.vel.x -= k * nx;
      ball.vel.y -= k * ny;
    }
    ball.lastTouch = { entity: p.id, team: p.team, tick };
  }
}
